import type { PropertySchemaRow } from "~/features/admin/categories/types";

/** The parts of an own property-schema row that take part in inheritance resolution. */
export interface OwnSchemaSummary {
  name: string;
  propertyTypeId: string;
  propertyAttributeTypeId: string | null;
}

export interface InheritedSchemaConflict {
  name: string;
  own: OwnSchemaSummary;
  inherited: PropertySchemaRow;
}

/**
 * Own rows that reuse an inherited key with a different type or attribute type.
 * Mirrors Cnut's rule: CONFLICT is type/attributeType only — required/unique
 * differences are merged (the strictest wins) and are not conflicts. The
 * inherited (shallowest) definition wins at read time, so the own row is
 * effectively ignored — that is what the form needs to warn about.
 */
export function findInheritedSchemaConflicts(
  ownSchemas: readonly OwnSchemaSummary[],
  inheritedRows: readonly PropertySchemaRow[],
): InheritedSchemaConflict[] {
  const inheritedByName = new Map(inheritedRows.map((row) => [row.name, row]));
  return ownSchemas.flatMap((own) => {
    if (!own.name) return [];
    const inherited = inheritedByName.get(own.name);
    if (!inherited) return [];
    const sameType = inherited.propertyTypeId === own.propertyTypeId;
    const sameAttribute = (inherited.propertyAttributeTypeId ?? null) === (own.propertyAttributeTypeId ?? null);
    return sameType && sameAttribute ? [] : [{ name: own.name, own, inherited }];
  });
}
